<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <span class="material-symbols-outlined">payments</span>
          </div>
          <h4 class="title">{{ $t("generalCosts.title") }}</h4>
          <md-button v-if="canEdit" class="md-success gc-save" :disabled="saving || !dirty || !valid" @click="save">{{ $t("generalCosts.save") }}</md-button>
        </md-card-header>
        <md-card-content>
          <p class="gc-intro">{{ $t("generalCosts.intro") }}</p>
          <p v-if="!canEdit" class="gc-intro">{{ $t("generalCosts.readOnly") }}</p>
          <div class="gc-table-wrap">
            <table class="gc-table">
              <thead>
                <tr>
                  <th>{{ $t("generalCosts.what") }}</th>
                  <th class="gc-value-col">{{ $t("generalCosts.value") }}</th>
                  <th>{{ $t("generalCosts.rule") }}</th>
                  <th>{{ $t("generalCosts.example") }}</th>
                  <th class="gc-note-col">{{ $t("generalCosts.note") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="row.key">
                  <td class="gc-name">{{ $t("generalCosts.keys." + row.key) }}</td>
                  <td class="gc-value-col">
                    <span class="gc-cell">
                      <input v-model.number="row.value" type="number" min="0" step="any" :disabled="!canEdit" :class="{ 'gc-invalid': !isValid(row) }" />
                      <span class="gc-unit">{{ row.unit }}</span>
                    </span>
                  </td>
                  <td class="gc-text">{{ $te("generalCosts.rules." + row.key) ? $t("generalCosts.rules." + row.key) : "" }}</td>
                  <td class="gc-text gc-example">{{ example(row.key) }}</td>
                  <td class="gc-note-col">
                    <input v-model="row.comment" type="text" maxlength="500" class="gc-note" :disabled="!canEdit" :placeholder="canEdit ? $t('generalCosts.notePlaceholder') : ''" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="gc-hint">{{ $t("generalCosts.exampleHint", { cbm: fmt(EXAMPLE_CBM), net: fmt(EXAMPLE_NET), gross: fmt(EXAMPLE_GROSS) }) }}</p>
        </md-card-content>
      </md-card>
    </div>
  </div>
</template>

<script>
import { hasPermission } from "../../mixins/auth";
import generalCostsService from "../../services/general-costs.service";

// Beispielprodukt und -preise wie in der Kalkulation_OCC.xlsx
const EXAMPLE_CBM = 0.89;
const EXAMPLE_NET = 69;
const EXAMPLE_GROSS = 79.95;
const DIVISORS = ["ContainerVolume", "PalletVolume", "LastNetAnnualRevenue"];

export default {
  data() {
    return { rows: [], original: "", saving: false, EXAMPLE_CBM, EXAMPLE_NET, EXAMPLE_GROSS };
  },
  computed: {
    canEdit() {
      return hasPermission("Write", "GeneralCosts", "Edit");
    },
    snapshot() {
      return JSON.stringify(this.rows.map((r) => [r.key, r.value, r.comment || ""]));
    },
    dirty() {
      return this.snapshot !== this.original;
    },
    valid() {
      return this.rows.every(this.isValid);
    },
    values() {
      return Object.fromEntries(this.rows.map((r) => [r.key, Number(r.value) || 0]));
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      return generalCostsService
        .getGeneralCosts()
        .then((items) => this.setRows(items))
        .catch(() => this.notify("danger", this.$t("generalCosts.loadFailed")));
    },
    setRows(items) {
      this.rows = items.map((i) => ({ key: i.key, value: i.value, unit: i.unit, comment: i.comment || "" }));
      this.$nextTick(() => {
        this.original = this.snapshot;
      });
    },
    isValid(row) {
      const v = row.value;
      if (v === "" || v == null || !Number.isFinite(Number(v)) || Number(v) < 0) return false;
      if (row.unit === "%" && Number(v) > 100) return false;
      return !DIVISORS.includes(row.key) || Number(v) > 0;
    },
    fmt(value, decimals = 2) {
      return this.$options.filters.formatNumberGerman(value, decimals);
    },
    example(key) {
      const v = this.values;
      const eur = (n) => `${this.fmt(n)} €`;
      switch (key) {
        case "ContainerUnloadingCost":
          return v.ContainerVolume > 0 ? `${this.fmt(v.ContainerUnloadingCost, 0)} € / ${this.fmt(v.ContainerVolume, 0)} cbm × ${this.fmt(EXAMPLE_CBM)} cbm = ${eur((v.ContainerUnloadingCost / v.ContainerVolume) * EXAMPLE_CBM)}` : "";
        case "PalletStorageCostPerMonth":
          return v.PalletVolume > 0 ? `${this.fmt(v.PalletStorageCostPerMonth)} € / ${this.fmt(v.PalletVolume, 3)} cbm × ${this.fmt(EXAMPLE_CBM)} cbm = ${eur((v.PalletStorageCostPerMonth / v.PalletVolume) * EXAMPLE_CBM)}` : "";
        case "FixedCostsPerYear":
          return v.LastNetAnnualRevenue > 0 ? `${this.fmt(v.FixedCostsPerYear, 0)} € / ${this.fmt(v.LastNetAnnualRevenue, 0)} € × ${this.fmt(EXAMPLE_NET)} € = ${eur((v.FixedCostsPerYear / v.LastNetAnnualRevenue) * EXAMPLE_NET)}` : "";
        case "AverageAcosPercent":
          return `${this.fmt(v.AverageAcosPercent)} % von ${this.fmt(EXAMPLE_GROSS)} € = ${eur((EXAMPLE_GROSS / 100) * v.AverageAcosPercent)}`;
        case "DefaultCommissionPercent":
          return `${this.fmt(v.DefaultCommissionPercent)} % von ${this.fmt(EXAMPLE_GROSS)} € = ${eur((EXAMPLE_GROSS / 100) * v.DefaultCommissionPercent)}`;
        case "ToolsCommissionPercent":
          return `${this.fmt(v.ToolsCommissionPercent)} % von ${this.fmt(EXAMPLE_GROSS)} € = ${eur((EXAMPLE_GROSS / 100) * v.ToolsCommissionPercent)}`;
        case "VatPercent":
          return `${this.fmt(EXAMPLE_GROSS)} € / ${this.fmt(100 + v.VatPercent)} × ${this.fmt(v.VatPercent)} = ${eur((EXAMPLE_GROSS / (100 + v.VatPercent)) * v.VatPercent)}`;
        case "PickCost":
          return eur(v.PickCost);
        case "PickSurchargeForwarding":
          return `${eur(v.PickCost)} + ${eur(v.PickSurchargeForwarding)} = ${eur(v.PickCost + v.PickSurchargeForwarding)}`;
        default:
          return "";
      }
    },
    save() {
      this.saving = true;
      const updates = this.rows.map((r) => ({ key: r.key, value: Number(r.value), comment: r.comment || null }));
      return generalCostsService
        .saveGeneralCosts(updates)
        .then((items) => {
          this.setRows(items);
          this.notify("success", this.$t("generalCosts.saved"));
        })
        .catch((e) => {
          if (e && e.response && e.response.status === 403) return;
          this.notify("danger", this.$t("generalCosts.saveFailed"));
        })
        .finally(() => {
          this.saving = false;
        });
    },
    notify(type, message) {
      this.$notify({ timeout: 2500, message, icon: "add_alert", horizontalAlign: "right", verticalAlign: "top", type });
    },
  },
};
</script>

<style scoped>
.md-card .md-card-header.md-card-header-icon .material-symbols-outlined {
  width: 33px;
  height: 33px;
  text-align: center;
  line-height: 33px;
  color: #fff;
  margin: 0 !important;
  font-size: 28px !important;
}
.gc-save {
  float: right;
  margin-top: -6px;
}
.gc-intro,
.gc-hint {
  color: #777;
  font-size: 13px;
  margin: 0 0 12px;
}
.gc-hint {
  margin: 12px 0 0;
  font-size: 12px;
}
.gc-table-wrap {
  overflow-x: auto;
}
.gc-table {
  width: 100%;
  border-collapse: collapse;
}
.gc-table th {
  text-align: left;
  font-size: 12px;
  font-weight: 500;
  color: #7f915a;
  padding: 6px 8px;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
}
.gc-table td {
  padding: 8px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}
.gc-name {
  font-weight: 500;
  min-width: 150px;
}
.gc-value-col {
  width: 170px;
}
.gc-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.gc-cell input {
  width: 100px;
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 3px;
  text-align: right;
}
.gc-cell input:disabled,
.gc-note:disabled {
  background: #f6f6f6;
  color: #555;
}
.gc-invalid {
  border-color: #f44336 !important;
}
.gc-unit {
  color: #777;
  font-size: 12px;
  min-width: 28px;
}
.gc-text {
  font-size: 13px;
  color: #555;
  min-width: 150px;
}
.gc-example {
  font-variant-numeric: tabular-nums;
}
.gc-note-col {
  min-width: 170px;
}
.gc-note {
  width: 100%;
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 3px;
}
</style>
